/**
 * JWT Token and API Request Mixin
 * Provides common JWT token management and API request functionality
 */
export default {
    data() {
        return {
            jwt_token: null,
            jwt_token_promise: null,
        };
    },

    methods: {
        /**
         * Get stored JWT token from localStorage with expiry check
         */
        getStoredJwtToken() {
            try {
                const storedTokenData = localStorage.getItem('jwt_token_data');
                if (!storedTokenData) return null;

                const tokenData = JSON.parse(storedTokenData);
                const now = Date.now();

                // Check if token is still valid (not expired)
                if (tokenData.expiry && now < tokenData.expiry) {
                    return tokenData.token;
                }

                // Token expired, remove from localStorage
                this.removeStoredJwtToken();
                return null;
            } catch (error) {
                // Invalid JSON, remove from localStorage
                this.removeStoredJwtToken();
                return null;
            }
        },

        /**
         * Store JWT token in localStorage with expiry timestamp
         */
        storeJwtToken(token) {
            const tokenData = {
                token: token,
                expiry: Date.now() + (55 * 60 * 1000) // 55 minutes in milliseconds
            };
            localStorage.setItem('jwt_token_data', JSON.stringify(tokenData));
        },

        /**
         * Remove JWT token from localStorage
         */
        removeStoredJwtToken() {
            localStorage.removeItem('jwt_token_data');
            // Also remove legacy format for backward compatibility
            localStorage.removeItem('jwt_token');
            localStorage.removeItem('jwt_token_timestamp');
        },

        /**
         * Get JWT token - checks memory cache, localStorage, or fetches new token
         */
        async getJwtToken() {
            // Return existing promise if already fetching
            if (this.jwt_token_promise) {
                return this.jwt_token_promise;
            }

            // Return cached token if available in memory
            if (this.jwt_token) {
                return this.jwt_token;
            }

            // Check localStorage for valid token
            const storedToken = this.getStoredJwtToken();
            if (storedToken) {
                this.jwt_token = storedToken;
                return this.jwt_token;
            }

            // Fetch JWT token
            this.jwt_token_promise = axios.get('/api/internal/users/exchange.json')
                .then(response => {
                    if (response.data && response.data.token) {
                        this.jwt_token = response.data.token;
                        this.storeJwtToken(this.jwt_token);
                        return this.jwt_token;
                    }
                    throw new Error('No token in response');
                })
                .catch(error => {
                    console.error('Failed to get JWT token:', error);
                    this.jwt_token = null;
                    this.removeStoredJwtToken();
                    throw error;
                })
                .finally(() => {
                    this.jwt_token_promise = null;
                });

            return this.jwt_token_promise;
        },

        /**
         * Make API request with JWT token handling and retry logic
         * @param {Function} requestFn - Function that takes Bearer token and makes the request
         */
        async makeApiRequest(requestFn) {
            const isExpiredTokenMessage = (message) =>
                message && (
                    message.includes('JWT') ||
                    message.toLowerCase().includes('expired token') ||
                    message.toLowerCase() === 'unauthenticated'
                );

            // Clears the cached/stored token and fetches a fresh one, then retries the original request.
            const refreshAndRetry = async () => {
                console.log('JWT token expired, refreshing and retrying...');
                this.jwt_token = null;
                this.jwt_token_promise = null;
                this.removeStoredJwtToken();
                const freshToken = await this.getJwtToken();
                return requestFn(`Bearer ${freshToken}`);
            };

            try {
                const jwtToken = await this.getJwtToken();
                const response = await requestFn(`Bearer ${jwtToken}`);

                // Case 1: The CakeDC API plugin returns HTTP 200 even for expired JWT errors,
                // so axios does not throw. We inspect the response body and trigger a refresh manually.
                if (response && response.data &&
                    response.data.status === 'error' &&
                    (isExpiredTokenMessage(response.data.message) || response.data.code === 401)) {
                    return refreshAndRetry();
                }

                return response;
            } catch (error) {
                // Case 2: Some endpoints do return HTTP 401/403 for JWT errors, which causes
                // axios to throw. We detect those here and trigger the same refresh logic.
                const isJwtError = error.response &&
                    (error.response.status === 401 ||
                     error.response.status === 403 ||
                     (error.response.data && (
                         isExpiredTokenMessage(error.response.data.message) ||
                         isExpiredTokenMessage(error.response.data.data && error.response.data.data.message)
                     )));

                if (isJwtError) {
                    return refreshAndRetry();
                }

                throw error;
            }
        },
    },
};


// WEBPACK FOOTER //
// ./src/Assets/mixins/ApiAuthMixin.js